/**
 * TrendChart — canvas 24h trend chart, multiple parameters overlaid.
 *
 * Different units share one plot, so each series is drawn on its own
 * min–max scale (the chart shows trend shape; exact values live in the
 * legend chips and the hover tooltip). Gaps in history stay gaps.
 */
const AXIS_H = 16;   // space for hour labels
const PAD = 6;       // vertical padding inside the plot area

export default class TrendChart {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.trend = null;
    this.params = [];
    this.resize();
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = w;
    this.h = h;
  }

  /** Nearest data point to canvas x; null when there is no data. */
  hover(px) {
    const tr = this.trend;
    if (!tr || !tr.times.length) return null;
    const t = this.t0 + (px / this.w) * (this.t1 - this.t0);
    let best = 0, bestD = Infinity;
    for (let i = 0; i < tr.times.length; i++) {
      const d = Math.abs(tr.times[i] - t);
      if (d < bestD) { bestD = d; best = i; }
    }
    const values = {};
    this.params.forEach(p => { values[p.key] = tr.series[p.key][best]; });
    return {
      index: best,
      x: ((tr.times[best] - this.t0) / (this.t1 - this.t0)) * this.w,
      time: tr.times[best],
      values: values,
    };
  }

  draw(trend, params, hoverIndex) {
    const { ctx, w, h } = this;
    if (!w || !h) return;
    this.trend = trend;
    this.params = params;

    const now = Date.now();
    this.t1 = now;
    this.t0 = now - 24 * 3600 * 1000;
    const plotH = h - AXIS_H;

    ctx.clearRect(0, 0, w, h);

    // Hour grid: a tick every 4h, labelled.
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.fillStyle = '#6b7683';
    ctx.font = '9px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.lineWidth = 1;
    const firstTick = Math.ceil(this.t0 / (4 * 3600 * 1000)) * 4 * 3600 * 1000;
    for (let t = firstTick; t <= this.t1; t += 4 * 3600 * 1000) {
      const x = Math.round(((t - this.t0) / (this.t1 - this.t0)) * w) + 0.5;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, plotH); ctx.stroke();
      ctx.fillText(new Date(t).getHours() + ':00', x, h - 4);
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath(); ctx.moveTo(0, plotH + 0.5); ctx.lineTo(w, plotH + 0.5); ctx.stroke();

    if (!trend || !trend.times.length) {
      ctx.fillStyle = '#6b7683';
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillText('Collecting trend data…', w / 2, plotH / 2);
      return;
    }

    const x = t => ((t - this.t0) / (this.t1 - this.t0)) * w;

    params.forEach(p => {
      const vals = trend.series[p.key];
      let lo = Infinity, hi = -Infinity;
      for (let i = 0; i < vals.length; i++) {
        const v = vals[i];
        if (v == null || trend.times[i] < this.t0) continue;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
      if (lo === Infinity) return;
      if (hi - lo < 1e-6) { hi += 0.5; lo -= 0.5; }
      const y = v => plotH - PAD - ((v - lo) / (hi - lo)) * (plotH - PAD * 2);

      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < vals.length; i++) {
        const t = trend.times[i];
        if (t < this.t0) continue;
        const v = vals[i];
        if (v == null) { pen = false; continue; }   // gap stays a gap
        if (!pen) { ctx.moveTo(x(t), y(v)); pen = true; }
        else ctx.lineTo(x(t), y(v));
      }
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.stroke();

      if (hoverIndex != null && vals[hoverIndex] != null && trend.times[hoverIndex] >= this.t0) {
        ctx.beginPath();
        ctx.arc(x(trend.times[hoverIndex]), y(vals[hoverIndex]), 3, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.strokeStyle = '#161a1f';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    });

    if (hoverIndex != null && trend.times[hoverIndex] >= this.t0) {
      const hx = Math.round(x(trend.times[hoverIndex])) + 0.5;
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(hx, 0); ctx.lineTo(hx, plotH); ctx.stroke();
    }
  }
}
